import { Injectable } from '@angular/core'
import { Observable, from } from 'rxjs'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { PointsApiService } from 'src/app/builder-services/api/points.api.service'
import { SidebarService } from 'src/app/sidebar/sidebar.service'

@Injectable({
  providedIn: 'root'
})
export class PointsService {
  constructor(
    private sidebarService: SidebarService,
    private builderService: BuilderService,
    private pointsApi: PointsApiService,
  ) {}

  private get projectId(): string {
    return String(this.builderService.selectedProject)
  }

  init_plugin() {
    this.sidebarService.setMenuItem([
      {
        name: 'points',
        icon: 'points',
        route: '/points'
      }
    ])
  }

  getPointsSetups(): Observable<any[]> {
    return from(this.pointsApi.getPointsSetups(this.projectId))
  }

  deletePointsSetup(setupId: string): Observable<void> {
    return from(this.pointsApi.deletePointsSystem(this.projectId, setupId))
  }

  setPointsEnabled(setupId: string, enabled: boolean): Observable<void> {
    return from(this.pointsApi.setPointsEnabled(this.projectId, setupId, enabled))
  }

  // ── Setup configuration ──────────────────────────────────────
  // Promise-returning, matching how the points screens already consume them.

  getPointsSystem(systemId: string): Promise<any | null> {
    return this.pointsApi.getPointsSystem(this.projectId, systemId)
  }

  getPointsSystemTargets(systemId: string, targetType: 'product' | 'category'): Promise<any[]> {
    return this.pointsApi.getPointsSystemTargets(this.projectId, systemId, targetType)
  }

  upsertPointsSystem(systemId: string | null, mainRow: Record<string, any>): Promise<string | null> {
    return this.pointsApi.upsertPointsSystem(this.projectId, systemId, mainRow)
  }

  upsertPointsTargets(
    systemId: string,
    targets: Array<{ target_type: 'category' | 'product'; target_id: string; checked: boolean; points: number }>,
  ): Promise<void> {
    return this.pointsApi.upsertPointsTargets(this.projectId, systemId, targets)
  }

  // ── Customization ────────────────────────────────────────────

  getPointsCustomization(): Promise<any | null> {
    return this.pointsApi.getPointsCustomization(this.projectId)
  }

  upsertPointsCustomization(fields: Record<string, any>): Promise<void> {
    return this.pointsApi.upsertPointsCustomization(this.projectId, fields)
  }

  // ── Balances and ledger ──────────────────────────────────────

  getAllUserPointsBalances(): Promise<any[]> {
    return this.pointsApi.getAllUserPointsBalances(this.projectId)
  }

  getUserPointsBalance(userId: string): Promise<number> {
    return this.pointsApi.getUserPointsBalance(this.projectId, userId)
  }

  getUserPointsLedger(userId: string): Promise<any[]> {
    return this.pointsApi.getUserPointsLedger(this.projectId, userId)
  }

  insertPointsLedgerEntries(entries: Array<Record<string, any>>): Promise<void> {
    return this.pointsApi.insertPointsLedgerEntries(this.projectId, entries)
  }

  /** The ledger rows one order produced — read by the order screen. */
  getOrderPointsLedger(orderId: string): Promise<{ source_type: string; points: number }[]> {
    return this.pointsApi.getOrderPointsLedger(this.projectId, orderId)
  }

  /** Removes the ledger rows tied to one order for a given source_type. */
  deleteOrderLedgerEntries(orderId: string, sourceType: string): Promise<void> {
    return this.pointsApi.deleteOrderLedgerEntries(this.projectId, orderId, sourceType)
  }
}
